// src/publicuser/pages/Learning.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import LearningNav from '../components/learning/LearningNav';
import LearningSideBar from '../components/learning/LearningSideBar';
import PostContent from '../components/learning/PostContent';
import CommentSection from '../components/learning/CommentSection';

import { categoryService } from '../../services/categoryService';
import { postService } from '../../services/postService';
import Footer from '../components/common/Footer'

import '../css/Learning.css';

const Learning = () => {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');

  const [allPosts, setAllPosts] = useState([]);
  const [regularPosts, setRegularPosts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  const [selectedPost, setSelectedPost] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);

  const createSlug = (title = '') =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const navigateToPost = useCallback(
    (post, replace = false) => {
      if (!post) return;

      navigate(
        `/learning-zone/${post.id}/${createSlug(post.title)}`,
        { replace }
      );
    },
    [navigate]
  );

  const currentPosts = isSearchMode ? searchResults : regularPosts;

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoryService.getAllTutorial();
        setCategories(response.data || []);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Load all posts
  useEffect(() => {
    const loadAllPosts = async () => {
      try {
        const response = await postService.getArticleSummary();
        const posts = response.data || [];

        setAllPosts(posts);
        setRegularPosts(posts);
        setSelectedPost(posts[0] || null);
        setCurrentIndex(0);
      } catch (error) {
        console.error('Failed to load all posts:', error);
        setAllPosts([]);
        setRegularPosts([]);
      }
    };

    loadAllPosts();
  }, []);

  // Search tutorials
  const handleSearch = useCallback(
    async (query) => {
      if (!query.trim()) {
        exitSearchMode();
        return;
      }

      setIsSearchLoading(true);
      setIsSearchMode(true);
      setSearchQuery(query);

      try {
        const response = await postService.searchTutorials(query);
        const results = response.data || [];

        setSearchResults(results);

        if (results.length > 0) {
          setSelectedPost(results[0]);
          setCurrentIndex(0);
          navigateToPost(results[0], true);
        } else {
          setSelectedPost(null);
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error('Failed to search tutorials:', error);
        setSearchResults([]);
        setSelectedPost(null);
      } finally {
        setIsSearchLoading(false);
      }
    },
    [navigateToPost]
  );

  // Exit search mode
  const exitSearchMode = useCallback(() => {
    setIsSearchMode(false);
    setSearchQuery('');
    setSearchResults([]);

    if (selectedCategoryId === 'all') {
      setRegularPosts(allPosts);

      if (allPosts.length > 0) {
        const exists = allPosts.some(
          (post) => post.id === selectedPost?.id
        );

        const targetPost = exists
          ? selectedPost
          : allPosts[0];

        setSelectedPost(targetPost);
        setCurrentIndex(
          allPosts.findIndex((p) => p.id === targetPost?.id) || 0
        );

        navigateToPost(targetPost, true);
      }
    } else {
      const loadCategoryPosts = async () => {
        try {
          const response =
            await postService.getTutorialsByCategory(
              selectedCategoryId
            );

          const posts = response.data || [];

          setRegularPosts(posts);

          if (posts.length > 0) {
            setSelectedPost(posts[0]);
            setCurrentIndex(0);
            navigateToPost(posts[0], true);
          } else {
            setSelectedPost(null);
          }
        } catch (error) {
          console.error('Failed to load posts:', error);
          setRegularPosts([]);
          setSelectedPost(null);
        }
      };

      loadCategoryPosts();
    }
  }, [
    selectedCategoryId,
    allPosts,
    selectedPost,
    navigateToPost,
  ]);

  // Select post
  const handlePostSelect = useCallback(
    (post, index) => {
      if (!post) return;

      const postIndex =
        index !== undefined
          ? index
          : currentPosts.findIndex((p) => p.id === post.id);

      setCurrentIndex(postIndex);
      setSelectedPost(post);

      navigateToPost(post);
      setIsMobileMenuOpen(false);
    },
    [currentPosts, navigateToPost]
  );

  // Select category
  const handleCategorySelect = useCallback(
    async (categoryId) => {
      if (isSearchMode) {
        setIsSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
      }

      setSelectedCategoryId(categoryId);
      setSelectedPost(null);
      setCurrentIndex(0);

      if (categoryId === 'all') {
        setRegularPosts(allPosts);

        if (allPosts.length > 0) {
          handlePostSelect(allPosts[0], 0);
        }

        return;
      }

      try {
        const response =
          await postService.getTutorialsByCategory(categoryId);

        const posts = response.data || [];

        setRegularPosts(posts);

        if (posts.length > 0) {
          handlePostSelect(posts[0], 0);
        }
      } catch (error) {
        console.error('Failed to load posts:', error);
        setRegularPosts([]);
      }
    },
    [allPosts, handlePostSelect, isSearchMode]
  );

  // Next post
  const handleNextPost = useCallback(() => {
    if (currentIndex >= currentPosts.length - 1) return;

    const nextIndex = currentIndex + 1;
    const nextPost = currentPosts[nextIndex];

    setCurrentIndex(nextIndex);
    setSelectedPost(nextPost);

    navigateToPost(nextPost);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentIndex, currentPosts, navigateToPost]);

  // Previous post
  const handlePreviousPost = useCallback(() => {
    if (currentIndex <= 0) return;

    const prevIndex = currentIndex - 1;
    const prevPost = currentPosts[prevIndex];

    setCurrentIndex(prevIndex);
    setSelectedPost(prevPost);

    navigateToPost(prevPost);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentIndex, currentPosts, navigateToPost]);

  // Load category posts
  useEffect(() => {
    if (isSearchMode) return;

    const loadPosts = async () => {
      if (selectedCategoryId === 'all') {
        setRegularPosts(allPosts);
        setCurrentIndex(0);
        setSelectedPost(allPosts[0] || null);
        return;
      }

      try {
        const response =
          await postService.getTutorialsByCategory(
            selectedCategoryId
          );

        const posts = response.data || [];

        setRegularPosts(posts);
        setCurrentIndex(0);
        setSelectedPost(posts[0] || null);
      } catch (error) {
        console.error('Failed to load posts:', error);
        setRegularPosts([]);
      }
    };

    loadPosts();
  }, [selectedCategoryId, allPosts, isSearchMode]);

  // Handle direct URL access
  useEffect(() => {
    if (!postId || isLoading) return;

    const findPost = async () => {
      let foundPost = currentPosts.find(
        (post) => post.id === parseInt(postId)
      );

      if (!foundPost && isSearchMode) {
        try {
          const response = await postService.getById(
            parseInt(postId)
          );

          foundPost = response.data;

          if (
            foundPost &&
            !searchResults.some((p) => p.id === foundPost.id)
          ) {
            setSearchResults((prev) => [foundPost, ...prev]);
          }
        } catch (error) {
          console.error('Failed to fetch post:', error);
        }
      }

      if (foundPost) {
        const index = currentPosts.findIndex(
          (post) => post.id === foundPost.id
        );

        setSelectedPost(foundPost);
        setCurrentIndex(index !== -1 ? index : 0);
      }
    };

    findPost();
  }, [
    postId,
    isLoading,
    isSearchMode,
    currentPosts,
    searchResults,
  ]);

  if (isLoading) {
    return (
      <div className="learn-loading">
        <div className="learn-spinner"></div>
        <p>Loading learning center...</p>
      </div>
    );
  }

  return (
    <div className="learn-page">
      <LearningNav
        categories={categories}
        selectedCategoryId={
          isSearchMode ? 'all' : selectedCategoryId
        }
        onCategorySelect={handleCategorySelect}
        onMobileMenuToggle={() =>
          setIsMobileMenuOpen(!isMobileMenuOpen)
        }
        isMobileMenuOpen={isMobileMenuOpen}
        onSearch={handleSearch}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        posts={currentPosts}
        isLoading={isSearchLoading}
        onPostClick={handlePostSelect}
      />

      <div className="learn-container">
        <LearningSideBar
          posts={currentPosts}
          selectedPost={selectedPost}
          onPostSelect={handlePostSelect}
          isMobileOpen={isMobileMenuOpen}
          isSearching={isSearchMode}
          searchQuery={searchQuery}
        />

        <main className="learn-main">
          <div className="learn-content-wrapper">
            {selectedPost ? (
              <div className="learn-content-container">
                <PostContent
                  postId={selectedPost.id}
                  onNext={handleNextPost}
                  onPrevious={handlePreviousPost}
                  hasNext={
                    currentIndex < currentPosts.length - 1
                  }
                  hasPrevious={currentIndex > 0}
                  currentPostTitle={selectedPost.title}
                  nextPostTitle={
                    currentIndex < currentPosts.length - 1
                      ? currentPosts[currentIndex + 1]?.title
                      : null
                  }
                  previousPostTitle={
                    currentIndex > 0
                      ? currentPosts[currentIndex - 1]?.title
                      : null
                  }
                />

                <CommentSection postId={selectedPost.id} />
              </div>
            ) : (
              <div className="learn-no-post">
                <div className="learn-empty-state">
                  <span className="learn-empty-icon">
                    {isSearchMode ? '🔍' : '📚'}
                  </span>

                  <h3>
                    {isSearchMode &&
                    currentPosts.length === 0
                      ? 'No results found'
                      : 'Select a tutorial to begin'}
                  </h3>

                  <p>
                    {isSearchMode &&
                    currentPosts.length === 0
                      ? `No tutorials found for "${searchQuery}". Try different keywords.`
                      : 'Choose a topic from the sidebar to start learning'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>

        <aside className="learn-right-sidebar">
          <div className="learn-right-content">
            <div className="learn-info-card">
              <div className="learn-card-icon">
                🤖 Free AI Tools
              </div>

              <p>
                Discover powerful AI tools designed to
                help with coding, content creation,
                productivity, and smarter learning.
              </p>

              <a
                href="/tools/AI"
                className="learn-card-link"
              >
                Explore AI Tools →
              </a>
            </div>

            <div className="learn-info-card">
              <div className="learn-card-icon">
                🎓 Free Certifications
              </div>

              <p>
                Access free technology courses,
                skill-based learning paths, and
                certification programs to grow your
                career.
              </p>

              <a
                href="/tools/Certification"
                className="learn-card-link"
              >
                View Certifications →
              </a>
            </div>

            <div className="learn-info-card">
              <div className="learn-card-icon">
                📰 Latest Blogs
              </div>

              <p>
                Explore trending tech blogs,
                development guides, tutorials, and the
                latest updates from the technology world.
              </p>

              <a
                href="/blogs"
                className="learn-card-link"
              >
                Read Latest Blogs →
              </a>
            </div>
          </div>
        </aside>
      </div>
      <Footer/>
    </div>
  );
};

export default Learning;